import { useRecoilValue } from "recoil";
import { SecretNetworkClient } from "secretjs";

import { useConfig } from "./useConfig";
import { useChainInfo } from "./useChainInfo";
import {
  Snip20ReferenceImplContract,
  Snip20ReferenceImplQueryContract,
} from "./clients/Snip20ReferenceImplContract";
import { baseDenom, defaultGas, sleepTime } from "../utils/constants";
import { walletState } from "../context/walletState";
import { coinConvert, sleep } from "../utils/common";
import { queryClientState } from "../context/queryClientState";
import { useMessageToaster } from "./useMessageToaster";
import { toast } from "react-toastify";

export const useSnip20Token = () => {
  const { queryClient } = useRecoilValue(queryClientState);
  const { client, address } = useRecoilValue(walletState);
  const config = useConfig();
  const chainInfo = useChainInfo();
  const toaster = useMessageToaster();

  const checkQueryClient = async () => {
    // while (true) {
    //   if (queryClient === undefined) {
    //     await sleep(sleepTime);
    //   } else {
    //     break;
    //   }
    // }
  }

  const checkTxnClient = async () => {
    // while (true) {
    //   if (client === undefined) {
    //     await sleep(sleepTime);
    //   } else {
    //     break;
    //   }
    // }
  }

  const checkAddressClient = async () => {
    // while (true) {
    //   if (address === undefined) {
    //     await sleep(sleepTime);
    //   } else {
    //     break;
    //   }
    // }
  }

  /*
   * Creates the snip20 token viewing key and stores in keplr
   */
  const createViewingKey = async (tokenName: string) => {
    await checkTxnClient();
    try {
      const result = await (window as any).keplr?.suggestToken(
        chainInfo.getChainId(),
        config.getAsset(tokenName).contract_addr as string
      );
      const balance = await getSnip20Balance(
        tokenName,
        result as unknown as string
      );

      return { result, balance };
    } catch (error) {
      // console.log(error);
      console.debug(error);
    }
  };


  /* 
   * unwrap the snip20 tokens
   */
 const unWrapToken = async(tokenName: string, amount:string, denom?: string)=>{
  await checkTxnClient();
  const tid = "Request Rejected";
    const snipClient = new Snip20ReferenceImplContract(
      client as SecretNetworkClient,
      config.getAsset(tokenName).contract_addr as string,
      config.getAsset(tokenName).contract_code_hash as string
    );
    
    try {
      const unWrapResponse = await snipClient.redeem(
        {
          userAddress: address as string,
          customFees: {gas: defaultGas.unwrap_token, amount:[]},
        },
        {
          amount: coinConvert(amount, 6, "machine"),
          denom: denom,
        }
      );
      if(unWrapResponse.code || unWrapResponse===undefined){
        toaster.Error("Failed to Unwrap token.");
        toaster.Error((unWrapResponse.rawLog).substr(0,100) + "...");
      }
    else toast.success(`UnWrap successfull`,{
      type: "success",
    });
      // console.log("unWrapResponse: ", unWrapResponse);
      return unWrapResponse;
    } catch (error) {
      // console.log(error);
      console.debug(error);
      toaster.Error("Failed to Unwrap token");
      toast.info(tid, {
        type: "error",
        closeOnClick: true,
        // render: "Request rejected",
        autoClose: 5000,
        isLoading: false,
      });
    }
  }

  // wrap token
 const wrapToken = async(tokenName: string, amount:string, denom?: string)=>{
  await checkTxnClient();
  const tid = "Request Rejected";
    const snipClient = new Snip20ReferenceImplContract(
      client as SecretNetworkClient,
      config.getAsset(tokenName).contract_addr as string,
      config.getAsset(tokenName).contract_code_hash as string
    );
    
    try {
      const WrapResponse = await snipClient.deposit(
        {
          userAddress: address as string,
          customFees: {gas: defaultGas.wrap_token, amount:[]},
          transferAmount:[{denom: baseDenom ,amount: coinConvert(amount, 6, "machine")}],
        },
        {
        }
      );
      if(WrapResponse.code || WrapResponse===undefined){
        toaster.Error("Failed to Wrap token.");
        toaster.Error((WrapResponse.rawLog).substr(0,100) + "...");
      }
    else toast.success(`Wrap successfull`,{
      type: "success",
    });
      return WrapResponse;
    } catch (error) {
      // console.log(error);
      console.debug(error);
      toaster.Error("Failed to Wrap token.");
      toast.info(tid, {
        type: "error",
        closeOnClick: true,
        // render: "Request rejected",
        autoClose: 5000,
        isLoading: false,
      });
    }
  }

  /*
   * Returns token balance using snip20 contract
   */
  const getSnip20Balance = async (
    tokenName: string,
    viewingKey: string | undefined
  ) => {
    await checkQueryClient();
    await checkAddressClient();
    console.log("reached here for ", tokenName, config.getAsset(tokenName).contract_addr as string,config.getAsset(tokenName).contract_code_hash as string)
    const snipQueryClient = new Snip20ReferenceImplQueryContract(
      queryClient as SecretNetworkClient,
      config.getAsset(tokenName).contract_addr as string,
      config.getAsset(tokenName).contract_code_hash as string
    );
    try {
      const balanceInfo = await snipQueryClient.balance({
        address: address as string,
        key: viewingKey as string,
      });

      return coinConvert(balanceInfo.balance.amount as string, 6, "human");
    } catch (error) {
      // console.log(error);
      console.debug(error);
    }
  };

  /*
   * Returns token balance for token address using snip20 contract
   */
  const getSnip20BalanceByAddress = async (
    tokenAddr: string,
    tokenCodeHash: string,
    viewingKey: string | undefined
  ) => {
    await checkQueryClient();
    await checkAddressClient();

    const snipQueryClient = new Snip20ReferenceImplQueryContract(
      queryClient as SecretNetworkClient,
      tokenAddr,
      tokenCodeHash
    );

    try {
      const balanceInfo = await snipQueryClient.balance({
        address: address as string,
        key: viewingKey as string,
      });

      return coinConvert(balanceInfo.balance.amount as string, 6, "human");
    } catch (error) {
      // console.log(error);
      console.debug(error);
    }
  };

  /*
   * Returns token total supply for token address using snip20 contract
   */
  const getSnip20SupplyByAddress = async (
    tokenAddr: string,
    tokenCodeHash: string,
  ) => {
    await checkQueryClient();
    const snipQueryClient = new Snip20ReferenceImplQueryContract(
      queryClient as SecretNetworkClient,
      tokenAddr,
      tokenCodeHash
    );

    try {
      const tokenInfo = await snipQueryClient.tokenInfo();

      return coinConvert(tokenInfo.token_info.total_supply as string, 6, "human");
    } catch (error) {
      // console.log(error);
      console.debug(error);
    }
  };

  // /*
  //  * Returns token info using snip20 contract
  //  */
  // const getTokenInfo = async (
  //   tokenName: string,
  // ) => {

  //   const routerQueryClient = new RouterQueryContract(
  //     queryClient,
  //     otherContracts.router.contract_addr,
  //     otherContracts.router.contract_code_hash
  //   );
  //   try {
  //     const routerConfig = await routerQueryClient.config();
  //   } catch (error) {
  //     console.log(error);
  //     //handleErrorMessage(error);
  //     return;
  //   }

  //   // let exch_rate = response.sejuno_exchange_rate.rate;
  //   // if (exch_rate.length > 10) {
  //   //   exch_rate = exch_rate.slice(0, 10);
  //   // }
  //   return [];
  // };

  // /*
  //  * Returns token config using snip20 contract
  //  */
  // const getTokenConfig = async (
  //   tokenName: string,
  // ) => {

  //   const routerQueryClient = new RouterQueryContract(
  //     queryClient,
  //     otherContracts.router.contract_addr,
  //     otherContracts.router.contract_code_hash
  //   );
  //   try {
  //     const routerConfig = await routerQueryClient.config();
  //   } catch (error) {
  //     console.log(error);
  //     //handleErrorMessage(error);
  //     return;
  //   }

  //   // let exch_rate = response.sejuno_exchange_rate.rate;
  //   // if (exch_rate.length > 10) {
  //   //   exch_rate = exch_rate.slice(0, 10);
  //   // }
  //   return [];
  // };

  // /*
  //  * Returns token allowance using snip20 contract
  //  */
  // const getAllowance = async (
  //   tokenName: string,
  //   userAddress: string,
  //   contractAddress: string,
  // ) => {

  //   const routerQueryClient = new RouterQueryContract(
  //     queryClient,
  //     otherContracts.router.contract_addr,
  //     otherContracts.router.contract_code_hash
  //   );
  //   try {
  //     const routerConfig = await routerQueryClient.config();
  //   } catch (error) {
  //     console.log(error);
  //     //handleErrorMessage(error);
  //     return;
  //   }

  //   // let exch_rate = response.sejuno_exchange_rate.rate;
  //   // if (exch_rate.length > 10) {
  //   //   exch_rate = exch_rate.slice(0, 10);
  //   // }
  //   return [];
  // };

  // /*
  //  * Returns token transaction history using snip20 contract
  //  */
  // const getTransactionHistory = async (
  //   tokenName: string,
  //   userAddress: string,
  // ) => {

  //   const routerQueryClient = new RouterQueryContract(
  //     queryClient,
  //     otherContracts.router.contract_addr,
  //     otherContracts.router.contract_code_hash
  //   );
  //   try {
  //     const routerConfig = await routerQueryClient.config();
  //   } catch (error) {
  //     console.log(error);
  //     //handleErrorMessage(error);
  //     return;
  //   }

  //   // let exch_rate = response.sejuno_exchange_rate.rate;
  //   // if (exch_rate.length > 10) {
  //   //   exch_rate = exch_rate.slice(0, 10);
  //   // }
  //   return [];
  // };

  /*
   * Transfers token to given address using snip20 contract
   */
  const doTransfer = async (
    tokenName: string,
    tokenAmount: string,
    destAddress: string,
    gasValue?: string | undefined,
  ) => {
    await checkTxnClient();
    const snipClient = new Snip20ReferenceImplContract(
      client as SecretNetworkClient,
      config.getAsset(tokenName).contract_addr as string,
      config.getAsset(tokenName).contract_code_hash as string
    );

    try {
      const transferResponse = await snipClient.transfer(
        {
          txnuserAddress: address as string,
          txnCustomFees: {
            gas: (gasValue !== undefined)? gasValue: defaultGas.snip_transfer,
            amount: [],
          }
        },
        {
          amount: coinConvert(tokenAmount, 6, "machine"),
          memo: "",
          recipient: destAddress,
        }
      );

      return transferResponse;
    } catch (error) {
      console.debug(error);
    }
  };

  /*
   * Sends token to given contract using snip20 contract
   */
  const doSend = async (
    tokenName: string,
    tokenAmount: string,
    contractAddress: string,
    contractCodeHash: string,
    msg?: string | undefined,
    gasValue?: string | undefined,
  ) => {
    await checkTxnClient();
    const snipClient = new Snip20ReferenceImplContract(
      client as SecretNetworkClient,
      config.getAsset(tokenName).contract_addr as string,
      config.getAsset(tokenName).contract_code_hash as string
    );

    try {
      const sendResponse = await snipClient.send(
        {
          txnuserAddress: address as string,
          txnCustomFees: {
            gas: (gasValue !== undefined)? gasValue: defaultGas.snip_send,
            amount: [],
          }
        },
        {
          amount: coinConvert(tokenAmount, 6, "machine"),
          memo: "",
          recipient: contractAddress,
          recipientCodeHash: contractCodeHash,
          msg: msg,
        }
      );

      return sendResponse;
    } catch (error) {
      console.debug(error);
    }
  }

  /*
   * Sends token to given contract using snip20 contract
   */
  const doSendByAddress = async (
    tokenAddress: string,
    tokenCodeHash: string,
    tokenAmount: string,
    contractAddress: string,
    contractCodeHash: string,
    msg?: string | undefined,
    gasValue?: string | undefined,
  ) => {
    await checkTxnClient();
    const snipClient = new Snip20ReferenceImplContract(
      client as SecretNetworkClient,
      tokenAddress,
      tokenCodeHash,
    );

    try {
      const sendResponse = await snipClient.send(
        {
          txnuserAddress: address as string,
          txnCustomFees: {
            gas: (gasValue !== undefined)? gasValue: defaultGas.snip_send,
            amount: [],
          }
        },
        {
          amount: coinConvert(tokenAmount, 6, "machine"),
          memo: "",
          recipient: contractAddress,
          recipientCodeHash: contractCodeHash,
          msg: msg,
        }
      );

      return sendResponse;
    } catch (error) {
      // console.log(error);
      console.debug(error);
    }
  }

  /*
   * Increase token allowance for given contract address using snip20 contract
   */
  const doIncreaseAllowance = async (
    tokenName: string,
    tokenAmount: string,
    contractAddress: string,
    gasValue?: string | undefined,
  ) => {
    await checkTxnClient();
    const snipClient = new Snip20ReferenceImplContract(
      client as SecretNetworkClient,
      config.getAsset(tokenName).contract_addr as string,
      config.getAsset(tokenName).contract_code_hash as string
    );

    try {
      const incAllowanceResponse = await snipClient.increaseAllowance(
        {
          userAddress: address as string,
          customFees: {
            gas: (gasValue !== undefined)? gasValue: defaultGas.snip_allowance,
            amount: [],
          }
        },
        {
          amount: coinConvert(tokenAmount, 6, "machine"),
          spender: contractAddress,
        }
      );

      return incAllowanceResponse;
    } catch (error) {
      // console.log(error);
      console.debug(error);
    }
  }

  return {
    getSnip20Balance,
    getSnip20BalanceByAddress,
    getSnip20SupplyByAddress,
    createViewingKey,
    unWrapToken,
    wrapToken,
    // getTokenInfo,
    // getTokenConfig,
    // getAllowance,
    // getTransactionHistory,
    doTransfer,
    doSend,
    doSendByAddress,
    doIncreaseAllowance,
  };
};
